export const EXECUTION_OUTCOMES = ["all", "success", "failure"] as const;

export type ExecutionOutcome = (typeof EXECUTION_OUTCOMES)[number];

export type ToolExecution = {
  id: string;
  created_at: string;
  updated_at: string;
  execution_type: string;
  execution_status: string;
  tool_name: string;
  toolkit_name: string;
  toolkit_version?: string;
  user_id?: string;
  run_at?: string;
  started_at?: string;
  finished_at?: string;
};

export type ToolExecutionError = {
  message: string;
  kind: string;
  can_retry: boolean;
  developer_message?: string;
  additional_prompt_content?: string;
  retry_after_ms?: number;
  status_code?: number;
  stacktrace?: string;
  extra?: Record<string, unknown>;
};

/**
 * Mirrors the engine's `auth.AuthorizationResponse`. The engine retains this
 * record even when outputs were not requested (it is an outcome, not a result
 * payload), so everything here reaches a reader holding only
 * `executions.metadataRead` unless the UI holds it back: `url` is a live
 * consent link into that end user's pending OAuth flow, and `context` is
 * declared with a `token` field.
 */
export type ToolExecutionAuthorization = {
  status?: string;
  provider_id?: string;
  scopes?: string[];
  url?: string;
  id?: string;
  user_id?: string;
  context?: Record<string, unknown>;
};

type ToolExecutionOutput = {
  value?: unknown;
  error?: ToolExecutionError;
  authorization?: ToolExecutionAuthorization;
};

export type ToolExecutionAttempt = {
  id: string;
  started_at?: string;
  finished_at?: string;
  success: boolean;
  output?: ToolExecutionOutput;
  system_error_message?: string;
};

/**
 * `input` is always present on the wire: `null` when the caller did not request
 * inputs, an object (possibly empty) when they did.
 */
export type ToolExecutionDetail = ToolExecution & {
  input: Record<string, unknown> | null;
  attempts: ToolExecutionAttempt[];
};

export type ToolExecutionsPage = {
  items: ToolExecution[];
  limit: number;
  offset: number;
  total_count: number;
};

/**
 * The project's execution-recording policy. `log_retention_days` describes a
 * real window only while `project_logging_enabled` is true: it is also 0 when
 * the engine cannot reach the policy source and falls back to not recording.
 */
export type ToolExecutionSettings = {
  project_logging_enabled: boolean;
  log_retention_days: number;
};
